/**
 * ConversationalForm — Budget Mantra web, one-question-at-a-time.
 *
 * Usage:
 *   <ConversationalForm
 *     questions={[
 *       { id: 'name', label: "What's your name?", type: 'text', required: true },
 *       { id: 'rent', label: "Monthly rent?", type: 'number', prefix: '₹' },
 *       { id: 'mode', label: "How do you pay?", type: 'select',
 *         options: [{ value: 'upi', label: 'UPI' }, { value: 'cash', label: 'Cash' }] },
 *     ]}
 *     values={form}
 *     onChange={(id, val) => setForm(p => ({ ...p, [id]: val }))}
 *     onComplete={() => setDone(true)}
 *     onBack={() => {}}        // optional — called when user backs out of step 0
 *     ctaLabel="Generate"
 *     saving={false}
 *   />
 */

import { useState, useEffect, useRef } from 'react';

// ── Types (JSDoc) ─────────────────────────────────────────────────────────────
// question: { id, label, sublabel, type, placeholder, required, optional,
//             prefix, suffix, options:[{value,label,emoji?}] }

// ── Progress bar ──────────────────────────────────────────────────────────────
function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="w-full h-1 bg-stone-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-orange-500 rounded-full transition-all duration-500 ease-out"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Select chips ──────────────────────────────────────────────────────────────
function SelectChips({ options, value, onChange, multi }) {
  const selected = multi ? (value || '').split(',').filter(Boolean) : [value].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-3 mt-2">
      {options.map(opt => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => {
              if (multi) {
                const next = active
                  ? selected.filter(v => v !== opt.value)
                  : [...selected, opt.value];
                onChange(next.join(','));
              } else {
                onChange(active ? '' : opt.value);
              }
            }}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl border-2 text-sm font-semibold transition-all duration-150
              ${active
                ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm'
                : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'
              }`}
          >
            {opt.emoji && <span className="text-lg">{opt.emoji}</span>}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Single question panel ─────────────────────────────────────────────────────
function QuestionPanel({ question, value, onChange, onNext, onBack, isLast, index, total, saving, canGoBack, renderCustom }) {
  const inputRef = useRef(null);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    setEntered(false);
    const t = setTimeout(() => {
      setEntered(true);
      if (question.type !== 'select' && question.type !== 'multiselect') {
        inputRef.current?.focus();
      }
    }, 30);
    return () => clearTimeout(t);
  }, [index]);

  const canProceed = !question.required || (value && value.toString().trim().length > 0);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && canProceed) {
      e.preventDefault();
      onNext();
    }
  };

  const inputClass = `w-full border-0 border-b-2 border-orange-400 bg-transparent text-stone-900
    focus:outline-none focus:border-orange-500 transition-colors
    text-2xl font-semibold placeholder:text-stone-300 py-2`;

  const renderInput = () => {
    // Allow parent to provide a fully custom input for special question types
    if (renderCustom) {
      const custom = renderCustom(question, value, onChange);
      if (custom) return custom;
    }
    switch (question.type) {
      case 'select':
        return <SelectChips options={question.options || []} value={value} onChange={onChange} multi={false} />;

      case 'multiselect':
        return <SelectChips options={question.options || []} value={value} onChange={onChange} multi={true} />;

      case 'textarea':
        return (
          <textarea
            ref={inputRef}
            value={value}
            onChange={e => onChange(e.target.value)}
            onKeyDown={handleKey}
            placeholder={question.placeholder || ''}
            rows={3}
            className={`${inputClass} resize-none text-lg leading-relaxed`}
          />
        );

      default:
        return (
          <div className="flex items-baseline gap-2">
            {question.prefix && (
              <span className="text-2xl font-bold text-stone-400">{question.prefix}</span>
            )}
            <input
              ref={inputRef}
              type={question.type === 'number' ? 'text' : question.type === 'date' ? 'date' : 'text'}
              inputMode={question.type === 'number' ? 'decimal' : undefined}
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKey}
              placeholder={question.placeholder || ''}
              className={`${inputClass} flex-1`}
            />
            {question.suffix && (
              <span className="text-lg font-semibold text-stone-400 whitespace-nowrap">{question.suffix}</span>
            )}
          </div>
        );
    }
  };

  return (
    <div
      className={`transition-all duration-300 ease-out ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      {/* Step counter */}
      <p className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">
        {index + 1} of {total}
      </p>

      {/* Question */}
      <h2 className="text-3xl font-extrabold text-stone-900 leading-tight mb-2 tracking-tight">
        {question.label}
      </h2>
      {question.sublabel && (
        <p className="text-sm text-stone-400 mb-6">{question.sublabel}</p>
      )}
      {!question.sublabel && <div className="mb-6" />}

      {/* Input */}
      {renderInput()}

      {/* Actions */}
      <div className="flex items-center gap-3 mt-10">
        {canGoBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-stone-200 text-sm font-semibold text-stone-500 hover:bg-stone-50 transition-colors"
          >
            ← Back
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed || saving}
          className={`flex items-center gap-2 px-8 py-3.5 rounded-xl text-sm font-bold transition-all duration-150
            ${canProceed && !saving
              ? 'bg-stone-900 text-white hover:bg-stone-800 shadow-md hover:shadow-lg active:scale-95'
              : 'bg-stone-100 text-stone-300 cursor-not-allowed'
            }`}
        >
          {saving && isLast ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="20" />
              </svg>
              Working…
            </span>
          ) : isLast ? (
            'Done ✓'
          ) : (
            <>Next <span className="text-base">→</span></>
          )}
        </button>
        {question.optional && !isLast && (
          <button
            type="button"
            onClick={onNext}
            className="text-sm text-stone-400 hover:text-stone-600 transition-colors ml-2"
          >
            Skip
          </button>
        )}
      </div>

      {/* Enter hint */}
      {question.type !== 'select' && question.type !== 'multiselect' && canProceed && (
        <p className="mt-3 text-xs text-stone-300">
          Press <kbd className="font-mono bg-stone-100 px-1.5 py-0.5 rounded text-stone-400">Enter ↵</kbd> to continue
        </p>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ConversationalForm({
  questions,
  values,
  onChange,
  onComplete,
  onBack,
  ctaLabel = 'Done',
  saving = false,
  renderCustom = null,
}) {
  const [step, setStep] = useState(0);

  // Reset to step 0 when values are cleared externally (new session)
  useEffect(() => {
    const allEmpty = questions.every(q => !values[q.id]);
    if (allEmpty) setStep(0);
  }, [values]);

  const current = questions[step];
  const isLast  = step === questions.length - 1;

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
    else onBack?.();
  };

  return (
    <div className="flex flex-col h-full min-h-[480px]">
      {/* Progress */}
      <div className="mb-8">
        <ProgressBar current={step} total={questions.length} />
      </div>

      {/* Question */}
      <div className="flex-1">
        <QuestionPanel
          key={step}
          question={current}
          value={values[current.id] || ''}
          onChange={(v) => onChange(current.id, v)}
          onNext={handleNext}
          onBack={handleBack}
          isLast={isLast}
          index={step}
          total={questions.length}
          saving={saving}
          canGoBack={step > 0}
          renderCustom={renderCustom}
        />
      </div>
    </div>
  );
}
